import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Modal, Segmented, Typography } from 'antd';
import { Music } from '@monorepo/types';

import './audio-modal.scss';
import { LoadingState, useLoading } from '@monorepo/react-components';
import LibraryAudio from './tabs/library-audio';
import MyAudio from './tabs/my-audio';
import { useVideoContext } from '../../hooks/use-video';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

export enum AudioTab {
  Library = 'Library',
  Upload = 'Upload',
}

interface Tab {
  value: string;
  label: string;
  disabled: boolean;
}

export interface AudioModalActions {
  open: () => void;
}

interface Props {
  sceneId: string;
  itemIndex: number;
  videoId: string;
}

const AudioModal = forwardRef<AudioModalActions, Props>(
  ({ sceneId, itemIndex, videoId }, ref) => {
    const { t } = useTranslation();
    const { loadingState, updateLoadingState } = useLoading();
    const { updateSceneItem } = useVideoContext();

    const tabs: Tab[] = [
      {
        label: t('audioModal.libraryTab'),
        value: AudioTab.Library,
        disabled: false,
      },
      {
        label: t('audioModal.uploadTab'),
        value: AudioTab.Upload,
        disabled: false,
      },
    ];

    const getAudioTabDescription = (tab: AudioTab): string => {
      const descriptions: Record<AudioTab, string> = {
        [AudioTab.Library]: t('audioModal.libraryDescription'),
        [AudioTab.Upload]: t('audioModal.uploadDescription'),
      };
      return descriptions[tab] || t('audioModal.defaultDescription');
    };
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState(AudioTab.Library);
    const [selectedAudioUrl, setSelectedAudioUrl] = useState<string | null>(
      null
    );
    const [selectedAudioName, setSelectedAudioName] = useState<string | null>(
      null
    );

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsOpen(true);
      },
    }));

    const handleOk = async () => {
      if (!selectedAudioUrl) {
        return;
      }

      await updateSceneItem({
        sceneId,
        videoId,
        itemIndex: itemIndex,
        updateDto: {
          data: {
            url: selectedAudioUrl,
            name: selectedAudioName || 'No Name',
          },
        },
      });
      handleCancel();
    };

    const handleCancel = () => {
      setIsOpen(false);
      setSelectedTab(AudioTab.Library);
    };

    const selectMusic = (audioUrl: string, name: string) => {
      setSelectedAudioUrl(audioUrl);
      setSelectedAudioName(name);
    };

    return (
      <Modal
        className="audio-modal"
        title={
          <div>
            <Title style={{ marginTop: 0, marginBottom: 0 }} level={4}>
              {t('audioModal.title')}
            </Title>
            <Text type="secondary" style={{ marginTop: 0 }}>
              {getAudioTabDescription(selectedTab)}
            </Text>
          </div>
        }
        open={isOpen}
        okText={t('audioModal.save')}
        onOk={handleOk}
        okButtonProps={{
          disabled: !selectedAudioUrl,
          loading: loadingState === LoadingState.Loading,
        }}
        onCancel={handleCancel}
        width={1000}
        styles={{
          body: {
            minHeight: 450,
          },
        }}
        style={{ overflow: 'auto' }}
      >
        <Segmented
          options={[...tabs]}
          value={selectedTab}
          onChange={(value) => setSelectedTab(value as AudioTab)}
          style={{ marginBottom: 16 }}
        />
        {selectedTab === AudioTab.Library ? (
          <LibraryAudio onSelect={selectMusic} />
        ) : null}
        {selectedTab === AudioTab.Upload ? (
          <MyAudio
            sceneId={sceneId}
            itemIndex={itemIndex}
            onSelect={selectMusic}
          />
        ) : null}
      </Modal>
    );
  }
);

export default AudioModal;
